import { LitElement, html, PropertyValues, TemplateResult } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { GzInputWebComponent } from '../../gzInput/component/GzInputWebComponent';
import { InlineMenuMixin } from '../../../mixins';
import { FormGroup } from '../../gzForm/formGroup/form-group';
import { GzFormFieldWebComponent } from '../../gzForm/formField';
import { Subscription } from 'rxjs';
import { GzIconButtonWebComponent } from '../../gzIconButton';
import { ListItemEvents } from '../types';
import {
  isElementOverflowed,
  isMouseEventOutsideElement,
  isMouseEventOutsideElementByTagName,
  OverflowDirection,
} from '../../../utils/htmlElement.utils';
import { GzTooltip } from '../../gzTooltip';
import { WEB_COMPONENT_GZ_CHECKBOX } from '../../../webcomponents.constants';
import { GzCheckboxWebComponent } from '../../gzCheckbox';
import { EventKeyManager } from '../../../utils/keys';

export class GzListItemWebComponent extends InlineMenuMixin(LitElement) {
  // see https://lit-element.polymer-project.org/guide/properties
  @property({ type: Boolean, reflect: true })
  active = false;

  @property({ type: Boolean, reflect: true })
  disabled = false;

  @property({ type: Boolean, reflect: true })
  noBackend = false;

  /**
   * This attribute removes the padding (left and right) from the element to make it more compact.
   * This should not be true for the draggable elements because will display visual issues
   */
  @property({ type: Boolean, reflect: true })
  isCompact = false;

  @property({ type: Boolean, reflect: true })
  emptyState = false;

  @property({ type: Boolean, reflect: true })
  editable = false;

  @property({ type: Boolean, reflect: true })
  noSpaceEscape = false;

  /**
   * Default input max length is 524288;
   */
  @property({ type: Number, attribute: true, reflect: true })
  inputMaxLength = 524288;

  @state() private formGroup: FormGroup | null;

  @state() private subscription: Subscription;

  @property({ type: Array, attribute: false })
  public errorMessage: Array<any>;

  @query('#input-field') formField: GzFormFieldWebComponent;

  @query('gz-input') inputComponent: GzInputWebComponent;

  @query('#save-input-btn') saveButton: GzIconButtonWebComponent;

  @query('slot')
  private slotElement: HTMLSlotElement;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  private boundDocumentClickHandler = this.documentClickHandler.bind(this);

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeDocumentClickListener();
  }
  connectedCallback() {
    super.connectedCallback();
    if (!this.editable) {
      this.addEventListener('click', this.handleClick);
    }
  }
  updated(properties: PropertyValues): void {
    if (properties.has('editable')) {
      this.handleUpdatedEditable();
    }
  }

  private handleUpdatedEditable(): void {
    if (this.editable) {
      this.createFormGroup();
      document.addEventListener('click', this.boundDocumentClickHandler);
    } else {
      this.formGroup?.unsubscribe();
      this.subscription?.unsubscribe();
    }
  }

  render() {
    return html`${this.editable ? this.renderEditable() : this.renderListItem()} `;
  }

  private renderListItem(): TemplateResult {
    return html` <div
        class="item-content"
        @mouseenter="${this.handleMouseEnter}"
        @mousemove="${GzTooltip.align}"
        @mouseleave="${GzTooltip.hide}"
        @click="${this.handleItemClick}"
      >
        <slot></slot>
      </div>
      ${this.renderInlineMenu()}`;
  }

  private renderEditable(): TemplateResult {
    return html`<div class="editable-container">
      <gz-form-field id="input-field" required>
        <gz-input
          type="text"
          id="inputTest"
          maxlength="${this.inputMaxLength}"
          gz-form-input
          value="${this.textContent}"
          inputwidth="full"
          @keydown="${this.onKeyDown}"
        ></gz-input>
      </gz-form-field>
      <gz-icon-button
        icon="ds-icon-notification_success"
        buttonColor="success"
        buttonFill="white"
        buttonType="round"
        fontSize="24"
        id="save-input-btn"
        @click="${this.saveEvent}"
      ></gz-icon-button>
      <gz-icon-button
        icon="ds-icon-delete_large"
        buttonColor="secondary"
        buttonFill="quiet"
        fontSize="24"
        class="cancel-icon-button"
        @click="${this.cancelEvent}"
      ></gz-icon-button>
    </div>`;
  }

  private documentClickHandler(event): void {
    const mouseClickedOutsideCurrentElement = isMouseEventOutsideElement(event, this);
    const mouseClickedOutsideSelectElement = isMouseEventOutsideElementByTagName(event, 'select-overlay');
    const mouseClickedOutside = mouseClickedOutsideCurrentElement && mouseClickedOutsideSelectElement;

    if (mouseClickedOutside) {
      this.cancelEvent(mouseClickedOutside);
      this.dispatchEvent(new CustomEvent(ListItemEvents.CLICK_OUTSIDE));
      this.removeDocumentClickListener();
    }
  }

  private removeDocumentClickListener(): void {
    document.removeEventListener('click', this.boundDocumentClickHandler);
    this.removeEventListener('click', this.handleClick);
  }

  private handleItemClick() {
    const itemClicked = new CustomEvent(ListItemEvents.ITEM_CLICK, {
      detail: {
        itemData: this.itemData,
      },
      bubbles: true,
    });
    this.dispatchEvent(itemClicked);
  }

  private saveEvent(): void {
    const inputValue = this.inputComponent.value;
    const eventSave = new CustomEvent(ListItemEvents.SAVE, {
      detail: {
        value: inputValue,
        itemData: this.itemData,
      },
      bubbles: true,
    });
    this.dispatchEvent(eventSave);
    this.editable = false;

    this.innerText = inputValue;
  }

  private cancelEvent(cancelFromClickOutside): void {
    const eventCancel = new CustomEvent(ListItemEvents.CANCEL, {
      detail: {
        cancelFromClickOutside: typeof cancelFromClickOutside === 'boolean' ? cancelFromClickOutside : false,
      },
    });
    this.dispatchEvent(eventCancel);
    this.editable = false;
  }

  private createFormGroup(): void {
    this.formField.Validators = this.errorMessage;

    this.formGroup = FormGroup.build({
      inputField: this.formField,
    });

    this.subscription = this.formGroup.valid$.subscribe({
      next: valid => {
        this.saveButton.disabled = !valid;
      },
    });

    setTimeout(() => {
      this.inputComponent.inputFocus();
    }, 100);
  }

  private handleMouseEnter(event: MouseEvent) {
    const label = event.target as HTMLElement;
    const textContent = this.slotElement.assignedNodes()[0].parentElement?.innerText;

    if (!textContent || !isElementOverflowed(label, OverflowDirection.HORIZONTAL)) {
      return;
    }

    GzTooltip.show(textContent, event);
  }

  private handleClick(event: Event) {
    if (event.target !== this) {
      return;
    }
    this.slotElement?.assignedElements().forEach((element: HTMLElement) => {
      const elementTag = element.tagName.toLowerCase();
      if (elementTag === WEB_COMPONENT_GZ_CHECKBOX && !(element as GzCheckboxWebComponent).disabled) {
        (element as GzCheckboxWebComponent).dispatchEvent(new Event('click'));
      }
    });
  }

  private onKeyDown(event: KeyboardEvent) {
    if (EventKeyManager.isEnter(event) && this.formGroup?.valid) {
      this.saveEvent();
    }
  }
}
